import { AdvanceAmount, TravelRequestSummary } from "../types";

/** "INR 50,000 + USD 200" - the one way an advance's amounts are shown in
 *  tables, pickers and detail views (same text as the backend's
 *  format_amounts in db/advances_store.py, used for emails and audit). */
export function formatAdvanceAmounts(amounts: AdvanceAmount[]): string {
  if (amounts.length === 0) return "-";
  return amounts
    .map((a) => `${a.currency} ${a.amount.toLocaleString()}`)
    .join(" + ");
}

/** The advance's amount in one currency, or null if it has no line in it -
 *  linking nets only the line in the request's own currency. */
export function advanceAmountIn(
  amounts: AdvanceAmount[],
  currency: string
): number | null {
  return amounts.find((a) => a.currency === currency)?.amount ?? null;
}

export interface NetPayableLine {
  currency: string;
  amount: number;
  /** "payable": the employee is still owed `amount`; "owed": the advance
   *  over-covered it and the employee owes `amount` back. */
  direction: "payable" | "owed";
}

/** Net payable per currency once the linked advance is netted off, the
 *  request's own currency first. What each currency is netted against:
 *  - The request's currency once Completed: the settled amount Finance
 *    entered (the trip's total, before the advance).
 *  - Otherwise the request's currency: the bills spent in it, PLUS the
 *    allowance on Fixed Allowance (confirmed desired behaviour - a Fixed
 *    Allowance employee is paid the allowance AND reimbursed for what they
 *    actually spent, same as Reimbursement's own bills-are-the-claim
 *    framing; bills are no longer tracking-only for either claim method).
 *  - Any other currency: the bills spent in it, for either claim method.
 *  Mirrored server-side by _net_payable_lines in notifications.py (reusing
 *  reports/request_report.py's _fx_ledger). */
export function netPayableByCurrency(
  request: Pick<
    TravelRequestSummary,
    | "claimMethod"
    | "currency"
    | "status"
    | "settledAmount"
    | "estimatedAllowanceAmount"
    | "expenses"
  >,
  advance: AdvanceAmount[]
): NetPayableLine[] {
  const fixed = request.claimMethod === "fixed_allowance";
  const spent = new Map<string, number>();
  for (const e of request.expenses) {
    const cur = e.currency || request.currency;
    spent.set(cur, (spent.get(cur) || 0) + e.amount);
  }
  const advanced = new Map(advance.map((a) => [a.currency, a.amount]));
  const ownBasis =
    request.status === "Completed" && request.settledAmount != null
      ? request.settledAmount
      : fixed
        ? request.estimatedAllowanceAmount + (spent.get(request.currency) || 0)
        : spent.get(request.currency) || 0;
  const others = new Set([...advanced.keys(), ...spent.keys()]);
  others.delete(request.currency);
  const currencies = [request.currency, ...[...others].sort()];
  return currencies.flatMap((cur) => {
    const basis = cur === request.currency ? ownBasis : spent.get(cur) || 0;
    const advancedHere = advanced.get(cur) || 0;
    if (basis === 0 && advancedHere === 0) return [];
    const diff = basis - advancedHere;
    return [
      {
        currency: cur,
        amount: Math.abs(diff),
        direction: diff >= 0 ? ("payable" as const) : ("owed" as const)
      }
    ];
  });
}
